import { CtaButton } from '@workspace/ui/components/dsm/cta-button'
import { SecondaryButton } from '@workspace/ui/components/dsm/secondary-button'
import { cn } from '@workspace/ui/lib/utils'
import { Briefcase, Check, CheckCircle2, Loader2, LogOut, Mail, Rocket, Upload } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import type { OnboardingApi } from '../lib/api.js'
import { OnboardingBrandPanel } from './onboarding-brand-panel.js'
import { OnboardingLayout } from './onboarding-layout.js'

type Step = 'email' | 'otp' | 'role' | 'linkedin' | 'website' | 'document'

/** Google "G" mark, inline so the button doesn't need a shared icon asset. */
function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="size-4" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M23.52 12.27c0-.85-.08-1.66-.22-2.45H12v4.64h6.47a5.53 5.53 0 0 1-2.4 3.63v3h3.88c2.27-2.09 3.57-5.17 3.57-8.82Z"
      />
      <path
        fill="#34A853"
        d="M12 24c3.24 0 5.96-1.07 7.95-2.91l-3.88-3c-1.08.72-2.46 1.15-4.07 1.15-3.13 0-5.78-2.11-6.73-4.96H1.27v3.09A12 12 0 0 0 12 24Z"
      />
      <path
        fill="#FBBC05"
        d="M5.27 14.28A7.2 7.2 0 0 1 4.89 12c0-.79.14-1.56.38-2.28V6.63H1.27A12 12 0 0 0 0 12c0 1.94.46 3.77 1.27 5.37l4-3.09Z"
      />
      <path
        fill="#EA4335"
        d="M12 4.75c1.76 0 3.34.6 4.58 1.79l3.44-3.44C17.95 1.19 15.24 0 12 0A12 12 0 0 0 1.27 6.63l4 3.09C6.22 6.86 8.87 4.75 12 4.75Z"
      />
    </svg>
  )
}

interface OnboardingFormProps {
  api: OnboardingApi
  sendOtp: (email: string) => Promise<void>
  verifyOtp: (email: string, otp: string) => Promise<boolean>
  /**
   * Starts the Google redirect flow. Omit to hide the button entirely (the
   * invite flow and the $slug pitch page both do this — the full-page
   * redirect would drop their in-progress state). Resolving with `{ error }`
   * (rather than throwing) covers the flag having just been turned off, or
   * the request being rejected outright.
   */
  onGoogleSignIn?: () => Promise<{ error?: unknown } | undefined>
  /**
   * A one-off message to show in the banner slot on mount (e.g. a Google
   * sign-in failure the caller decoded from the redirect's `?error=`). Unlike
   * `error`, this is never cleared by the authenticated-state reset effect —
   * only by the next user action, so a Google failure survives the redirect
   * back into this form.
   */
  notice?: string
  onSubmit: (data: {
    email: string
    linkedinUrl: string | null
    websiteUrl: string | null
    documentId: string | null
    documentName: string | null
    role: 'founder' | 'investor'
  }) => void
  /** When true, skip email/OTP steps (user is already authenticated) */
  authenticated?: boolean
  /** Pre-fill email when authenticated */
  userEmail?: string
  /** Pre-fill role when already set (e.g. from backend) */
  userRole?: 'founder' | 'investor' | null
  /** Called when authenticated user clicks email to sign out */
  onSignOut?: () => void
  /** Invite token — when set, user is joining via invite */
  inviteToken?: string
  /** Pre-filled email from invite lookup */
  inviteEmail?: string
  /** Role inferred from invite (founder for startup invites, investor for investor invites) */
  inviteRole?: 'founder' | 'investor'
  /** When true, skip the document upload step (file already provided externally) */
  skipDocumentStep?: boolean
  /** Custom left panel content (replaces default branding panel) */
  leftPanel?: React.ReactNode
  /** Custom title for the email step (replaces "Welcome to SeedForge") */
  formTitle?: string
  /** Custom subtitle for the email step (replaces "Enter your email to get started") */
  formSubtitle?: string
}

function CompletedStep({
  value,
  onEdit,
  opacity = 1,
}: {
  value: string
  onEdit: () => void
  opacity?: number
}) {
  return (
    <button
      type="button"
      onClick={onEdit}
      className="group flex w-full items-center justify-center gap-2 rounded-lg py-2 transition-colors hover:bg-white/[0.03]"
      style={{ opacity }}
    >
      <Check className="size-4 shrink-0 text-emerald-500" />
      <span className="truncate text-sm font-medium text-foreground">{value}</span>
    </button>
  )
}

function OtpInput({
  onComplete,
  disabled,
}: {
  onComplete: (otp: string) => void
  disabled?: boolean
}) {
  const [digits, setDigits] = useState<string[]>(Array(6).fill(''))
  const digitsRef = useRef<string[]>(Array(6).fill(''))
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  useEffect(() => {
    inputRefs.current[0]?.focus()
  }, [])

  function handleChange(index: number, value: string) {
    if (!/^\d*$/.test(value)) return
    const char = value.slice(-1)
    const next = [...digitsRef.current]
    next[index] = char
    digitsRef.current = next
    setDigits(next)

    if (char && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }

    if (next.every((d) => d !== '')) {
      onComplete(next.join(''))
    }
  }

  function handleKeyDown(index: number, e: React.KeyboardEvent) {
    if (e.key === 'Backspace' && !digitsRef.current[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  function handlePaste(e: React.ClipboardEvent) {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    if (!pasted) return
    const next = [...digitsRef.current]
    for (let i = 0; i < pasted.length; i++) {
      next[i] = pasted[i] ?? ''
    }
    digitsRef.current = next
    setDigits(next)
    const focusIdx = Math.min(pasted.length, 5)
    inputRefs.current[focusIdx]?.focus()
    if (next.every((d) => d !== '')) {
      onComplete(next.join(''))
    }
  }

  const positions = ['a', 'b', 'c', 'd', 'e', 'f'] as const

  return (
    <div className="flex gap-2">
      {digits.map((digit, i) => (
        <input
          key={positions[i]}
          ref={(el) => {
            inputRefs.current[i] = el
          }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digit}
          disabled={disabled}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={i === 0 ? handlePaste : undefined}
          className={cn(
            'flex size-11 items-center justify-center rounded-lg border bg-card text-center text-lg font-semibold text-foreground transition-colors focus:outline-none focus:ring-1',
            digit
              ? 'border-[#f97316]/50 focus:border-[#f97316] focus:ring-[#f97316]'
              : 'border-border focus:border-[#f97316] focus:ring-[#f97316]'
          )}
        />
      ))}
    </div>
  )
}

export function OnboardingForm({
  api,
  sendOtp,
  verifyOtp,
  onGoogleSignIn,
  notice,
  onSubmit,
  authenticated,
  userEmail,
  userRole,
  onSignOut,
  inviteToken,
  inviteEmail,
  inviteRole,
  skipDocumentStep,
  leftPanel,
  formTitle,
  formSubtitle,
}: OnboardingFormProps) {
  const isInviteFlow = !!inviteToken
  const resolvedInviteRole = inviteRole ?? 'founder'
  const [email, setEmail] = useState(inviteEmail ?? userEmail ?? '')
  const [emailConfirmed, setEmailConfirmed] = useState(!!authenticated)
  const [otpVerified, setOtpVerified] = useState(!!authenticated)
  const [role, setRole] = useState<'founder' | 'investor' | null>(
    userRole ?? (isInviteFlow ? resolvedInviteRole : null)
  )
  const [linkedin, setLinkedin] = useState('')
  const [linkedinConfirmed, setLinkedinConfirmed] = useState(false)
  const [website, setWebsite] = useState('')
  const [websiteConfirmed, setWebsiteConfirmed] = useState(false)
  const [uploadedFile, setUploadedFile] = useState<{ name: string; documentId: string } | null>(
    null
  )

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [googleLoading, setGoogleLoading] = useState(false)
  const googleLoadingRef = useRef(false)
  // Kept separate from `error`: the reset effect below clears `error` whenever
  // `authenticated` flips, which would otherwise wipe a Google failure banner
  // the instant the redirect lands back here unauthenticated.
  const [noticeMessage, setNoticeMessage] = useState<string | null>(notice ?? null)
  const emailInputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (!isInviteFlow && emailInputRef.current) emailInputRef.current.focus()
  }, [isInviteFlow])

  useEffect(() => {
    setNoticeMessage(notice ?? null)
  }, [notice])

  // A browser-back from Google's consent screen can restore this page from
  // bfcache with the button still mid-click.
  useEffect(() => {
    function handlePageShow(e: PageTransitionEvent) {
      if (e.persisted) {
        googleLoadingRef.current = false
        setGoogleLoading(false)
      }
    }
    window.addEventListener('pageshow', handlePageShow)
    return () => window.removeEventListener('pageshow', handlePageShow)
  }, [])

  useEffect(() => {
    if (authenticated) {
      setEmailConfirmed(true)
      setOtpVerified(true)
      if (userEmail) setEmail(userEmail)
    } else {
      setEmail(inviteEmail ?? '')
      setEmailConfirmed(false)
      setOtpVerified(false)
      setRole(isInviteFlow ? resolvedInviteRole : null)
      setLinkedin('')
      setLinkedinConfirmed(false)
      setWebsite('')
      setWebsiteConfirmed(false)
      setError(null)
    }
  }, [authenticated, userEmail, inviteEmail, isInviteFlow, resolvedInviteRole])

  useEffect(() => {
    if (userRole) setRole(userRole)
    else if (isInviteFlow) setRole(resolvedInviteRole)
  }, [userRole, isInviteFlow, resolvedInviteRole])

  const fileInputRef = useRef<HTMLInputElement>(null)

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  const linkedinValid =
    /linkedin\.com\/in\/.+/.test(linkedin) || /^[a-zA-Z0-9-]{3,}$/.test(linkedin.trim())
  const resolvedLinkedinUrl = linkedin
    ? linkedin.includes('linkedin.com')
      ? linkedin
      : `https://www.linkedin.com/in/${linkedin.trim()}`
    : null

  const websiteValid = /^(https?:\/\/)?[\w.-]+\.[a-z]{2,}(\/.*)?$/i.test(website.trim())
  const resolvedWebsiteUrl = website.trim()
    ? website.includes('://')
      ? website.trim()
      : `https://${website.trim()}`
    : null

  const currentStep: Step = !emailConfirmed
    ? 'email'
    : !otpVerified
      ? 'otp'
      : !role
        ? 'role'
        : !linkedinConfirmed
          ? 'linkedin'
          : !websiteConfirmed
            ? 'website'
            : 'document'

  // For invite flow: auto-submit after LinkedIn confirmation (skip remaining steps)
  const linkedinSubmittedRef = useRef(false)
  useEffect(() => {
    if (
      isInviteFlow &&
      linkedinConfirmed &&
      currentStep === 'website' &&
      role &&
      !linkedinSubmittedRef.current
    ) {
      linkedinSubmittedRef.current = true
      onSubmit({
        email,
        linkedinUrl: resolvedLinkedinUrl,
        websiteUrl: null,
        documentId: null,
        documentName: null,
        role,
      })
    }
  }, [isInviteFlow, linkedinConfirmed, currentStep, email, resolvedLinkedinUrl, role, onSubmit])

  function resetToEmail() {
    if (onSignOut) {
      onSignOut()
      return
    }
    setEmailConfirmed(false)
    setOtpVerified(false)
    setRole(null)
    setLinkedin('')
    setLinkedinConfirmed(false)
    setWebsite('')
    setWebsiteConfirmed(false)
    setUploadedFile(null)
    setError(null)
  }

  function resetToRole() {
    setRole(null)
    setLinkedin('')
    setLinkedinConfirmed(false)
    setWebsite('')
    setWebsiteConfirmed(false)
    setUploadedFile(null)
    setError(null)
  }

  function resetToLinkedin() {
    setLinkedinConfirmed(false)
    setWebsiteConfirmed(false)
    setUploadedFile(null)
    setError(null)
  }

  function resetToWebsite() {
    setWebsiteConfirmed(false)
    setUploadedFile(null)
  }

  function confirmWebsite() {
    if (skipDocumentStep && role) {
      onSubmit({
        email,
        linkedinUrl: resolvedLinkedinUrl,
        websiteUrl: resolvedWebsiteUrl,
        documentId: null,
        documentName: null,
        role,
      })
      return
    }
    setWebsiteConfirmed(true)
  }

  const handleSendOtp = useCallback(async () => {
    setLoading(true)
    setError(null)
    setNoticeMessage(null)
    try {
      await sendOtp(email)
      setEmailConfirmed(true)
    } catch {
      setError('Failed to send verification code. Please try again.')
    } finally {
      setLoading(false)
    }
  }, [email, sendOtp])

  const handleGoogleSignIn = useCallback(async () => {
    // `googleLoadingRef` (not the `googleLoading` state) is what actually
    // stops a double-click: two click events dispatched back to back can both
    // run before React re-renders with the state update, so a state-only
    // guard lets both through. The ref is set synchronously, before anything
    // async happens.
    if (!onGoogleSignIn || googleLoadingRef.current) return
    googleLoadingRef.current = true
    setError(null)
    setNoticeMessage(null)
    setGoogleLoading(true)
    try {
      const result = await onGoogleSignIn()
      // A successful call navigates the browser away to Google — this only
      // runs when it was rejected instead (e.g. the flag was just turned off).
      if (result?.error) {
        setError('Google sign-in is unavailable — continue with your email instead.')
        googleLoadingRef.current = false
        setGoogleLoading(false)
      }
    } catch {
      setError('Google sign-in is unavailable — continue with your email instead.')
      googleLoadingRef.current = false
      setGoogleLoading(false)
    }
  }, [onGoogleSignIn])

  // For invite flow: auto-send OTP when email is pre-filled
  const otpSentRef = useRef(false)
  useEffect(() => {
    if (isInviteFlow && inviteEmail && !authenticated && !emailConfirmed && !otpSentRef.current) {
      otpSentRef.current = true
      handleSendOtp()
    }
  }, [isInviteFlow, inviteEmail, authenticated, emailConfirmed, handleSendOtp])

  const handleVerifyOtp = useCallback(
    async (otp: string) => {
      setLoading(true)
      setError(null)
      try {
        const success = await verifyOtp(email, otp)
        if (success) {
          setOtpVerified(true)
          // For invite flow: auto-set role based on invite type after OTP verification
          if (isInviteFlow) {
            await api.setRole(resolvedInviteRole)
          }
        } else {
          setError('Invalid code. Please try again.')
        }
      } catch {
        setError('Verification failed. Please try again.')
      } finally {
        setLoading(false)
      }
    },
    [email, verifyOtp, isInviteFlow, resolvedInviteRole, api]
  )

  const handleSelectRole = useCallback(
    async (selectedRole: 'founder' | 'investor') => {
      setLoading(true)
      setError(null)
      try {
        await api.setRole(selectedRole)
        setRole(selectedRole)
      } catch {
        setError('Failed to set role. Please try again.')
      } finally {
        setLoading(false)
      }
    },
    [api]
  )

  const handleLinkedinConfirm = useCallback(() => {
    setLinkedinConfirmed(true)
  }, [])

  const handleSkipLinkedin = useCallback(() => {
    setLinkedinConfirmed(true)
  }, [])

  const handleFileUpload = useCallback(
    async (file: File) => {
      setLoading(true)
      setError(null)
      try {
        const doc = await api.createDocument(file.name)
        await api.uploadToPresignedUrl(doc.uploadUrl, doc.uploadFields, file)
        await api.completeDocumentUpload(doc.id)
        setUploadedFile({ name: file.name, documentId: doc.id })
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to upload pitch deck. Please try again.'
        )
      } finally {
        setLoading(false)
      }
    },
    [api]
  )

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (file) handleFileUpload(file)
    },
    [handleFileUpload]
  )

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      const file = e.dataTransfer.files?.[0]
      if (file) handleFileUpload(file)
    },
    [handleFileUpload]
  )

  return (
    <OnboardingLayout variant="raw">
      {/* Left panel */}
      {leftPanel ?? <OnboardingBrandPanel audience={role} />}

      {/* Right panel — form */}
      <div className="flex flex-1 items-center justify-center p-8">
        <div className="relative w-full max-w-sm">
          {/* Completed steps floating above */}
          {otpVerified && currentStep !== 'email' && currentStep !== 'otp' && (
            <div className="absolute bottom-full left-0 right-0 space-y-1 pb-6">
              {/* Email completed step with logout */}
              <div className="flex w-full items-center justify-center gap-2 rounded-lg py-2">
                <Check className="size-4 shrink-0 text-emerald-500" />
                <span className="truncate text-sm font-medium text-foreground">{email}</span>
                {onSignOut && (
                  <button
                    type="button"
                    onClick={onSignOut}
                    className="ml-1 rounded-md p-1 text-muted-foreground transition-colors hover:bg-white/[0.06] hover:text-foreground"
                  >
                    <LogOut className="size-3.5" />
                  </button>
                )}
              </div>
              {role && !skipDocumentStep && (
                <CompletedStep
                  value={role === 'founder' ? 'Founder' : 'Investor'}
                  onEdit={resetToRole}
                  opacity={linkedinConfirmed ? 0.5 : 1}
                />
              )}
              {linkedinConfirmed && (
                <CompletedStep
                  value={
                    linkedin
                      .replace(/https?:\/\/(www\.)?linkedin\.com\/in\//, '')
                      .replace(/\/$/, '') || 'Skipped'
                  }
                  onEdit={resetToLinkedin}
                />
              )}
              {websiteConfirmed && (
                <CompletedStep
                  value={
                    website ? new URL(resolvedWebsiteUrl ?? 'https://x.com').hostname : 'Skipped'
                  }
                  onEdit={resetToWebsite}
                />
              )}
            </div>
          )}

          <div>
            <div className="mb-6 lg:hidden">
              <img src="/seedforge-logo.svg" alt="SeedForge" className="h-7" />
            </div>

            {/* Error / notice banner */}
            {(error ?? noticeMessage) && (
              <div className="mb-4 rounded-lg border border-red-500/20 bg-red-500/10 px-4 py-2.5 text-sm text-red-400">
                {error ?? noticeMessage}
              </div>
            )}

            <div className="space-y-3">
              {/* STEP 1: Email */}
              {currentStep === 'email' && (
                <div className="animate-in fade-in slide-in-from-bottom-3 duration-400 ease-out">
                  <h2 className="text-center text-xl font-semibold text-foreground">
                    {formTitle ?? 'Welcome to SeedForge'}
                  </h2>
                  <p className="mt-1 text-center text-sm text-muted-foreground">
                    {formSubtitle ?? 'Enter your email to get started'}
                  </p>

                  <div className="mt-5 space-y-3">
                    <div className="relative">
                      <Mail className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                      <input
                        ref={emailInputRef}
                        type="email"
                        value={email}
                        onChange={(e) => !isInviteFlow && setEmail(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && emailValid && handleSendOtp()}
                        placeholder="you@company.com"
                        readOnly={isInviteFlow}
                        className={cn(
                          'flex h-10 w-full rounded-md border border-border bg-card pl-10 pr-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-[#f97316] focus:outline-none focus:ring-1 focus:ring-[#f97316] transition-colors',
                          isInviteFlow && 'opacity-70'
                        )}
                      />
                    </div>
                    <CtaButton
                      className="w-full"
                      disabled={!emailValid}
                      loading={loading}
                      arrow
                      onClick={handleSendOtp}
                    >
                      Continue with email
                    </CtaButton>
                    <p className="text-center text-xs text-muted-foreground/80">
                      We'll send you a verification code
                    </p>

                    {onGoogleSignIn && !isInviteFlow && (
                      <>
                        <div className="flex items-center gap-3 pt-1">
                          <div className="h-px flex-1 bg-border" />
                          <span className="text-xs text-muted-foreground/80">or</span>
                          <div className="h-px flex-1 bg-border" />
                        </div>
                        <SecondaryButton
                          className="w-full"
                          icon={<GoogleIcon />}
                          loading={googleLoading}
                          disabled={loading}
                          onClick={handleGoogleSignIn}
                        >
                          Continue with Google
                        </SecondaryButton>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* STEP 2: OTP */}
              {currentStep === 'otp' && (
                <div className="animate-in fade-in slide-in-from-bottom-3 duration-400 ease-out">
                  <h2 className="text-center text-xl font-semibold text-foreground">
                    Check your email
                  </h2>
                  <p className="mt-1 text-center text-sm text-muted-foreground">
                    We sent a 6-digit code to{' '}
                    <span className="font-medium text-foreground">{email}</span>
                  </p>

                  <div className="mt-5 space-y-4">
                    <div className="flex justify-center">
                      <OtpInput onComplete={handleVerifyOtp} disabled={loading} />
                    </div>
                    {loading && (
                      <div className="flex justify-center">
                        <Loader2 className="size-4 animate-spin text-[#f97316]" />
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <button
                        type="button"
                        onClick={resetToEmail}
                        className="text-xs text-muted-foreground transition-colors hover:text-foreground"
                      >
                        Wrong email?
                      </button>
                      <button
                        type="button"
                        disabled={loading}
                        onClick={handleSendOtp}
                        className="text-xs text-[#f97316] transition-colors hover:text-[#f97316]/80 disabled:opacity-40"
                      >
                        Resend code
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* STEP 3: Role */}
              {currentStep === 'role' && (
                <div className="animate-in fade-in slide-in-from-bottom-3 duration-400 ease-out">
                  <h2 className="text-center text-xl font-semibold text-foreground">
                    Choose how you'll use SeedForge
                  </h2>

                  <div className="mt-5 space-y-3">
                    <button
                      type="button"
                      disabled={loading}
                      onClick={() => handleSelectRole('founder')}
                      className="flex w-full items-center gap-4 rounded-xl border border-border bg-card/30 px-5 py-4 text-left transition-all hover:border-muted-foreground/30 disabled:opacity-40"
                    >
                      <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-card text-muted-foreground">
                        {loading ? (
                          <Loader2 className="size-5 animate-spin" />
                        ) : (
                          <Rocket className="size-5" />
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">Founder</p>
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          Get your startup evaluated and practice pitching
                        </p>
                      </div>
                    </button>

                    <button
                      type="button"
                      disabled={loading}
                      onClick={() => handleSelectRole('investor')}
                      className="flex w-full items-center gap-4 rounded-xl border border-border bg-card/30 px-5 py-4 text-left transition-all hover:border-muted-foreground/30 disabled:opacity-40"
                    >
                      <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-card text-muted-foreground">
                        {loading ? (
                          <Loader2 className="size-5 animate-spin" />
                        ) : (
                          <Briefcase className="size-5" />
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">Investor</p>
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          Discover and evaluate pre-vetted startups
                        </p>
                      </div>
                    </button>
                  </div>
                </div>
              )}

              {/* STEP 4: LinkedIn */}
              {currentStep === 'linkedin' && (
                <div className="animate-in fade-in slide-in-from-bottom-3 duration-400 ease-out">
                  <div className="space-y-2">
                    <span className="text-sm font-medium text-foreground">Your LinkedIn</span>
                    <div className="relative">
                      <input
                        type="text"
                        value={linkedin}
                        onChange={(e) => setLinkedin(e.target.value)}
                        onKeyDown={(e) =>
                          e.key === 'Enter' && linkedinValid && handleLinkedinConfirm()
                        }
                        placeholder="yourprofile or linkedin.com/in/yourprofile"
                        className={cn(
                          'flex h-10 w-full rounded-md border bg-card px-3 py-2 pr-10 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 transition-colors',
                          linkedinValid
                            ? 'border-emerald-500/50 focus:border-emerald-500 focus:ring-emerald-500/30'
                            : 'border-border focus:border-[#f97316] focus:ring-[#f97316]'
                        )}
                      />
                      {linkedinValid && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 animate-in fade-in zoom-in-50 duration-200">
                          <CheckCircle2 className="size-5 text-emerald-500" />
                        </div>
                      )}
                    </div>
                    <CtaButton
                      className="mt-1 w-full"
                      disabled={!linkedinValid}
                      loading={loading}
                      arrow
                      onClick={handleLinkedinConfirm}
                    >
                      Continue
                    </CtaButton>
                    <button
                      type="button"
                      onClick={handleSkipLinkedin}
                      className="w-full text-center text-xs text-muted-foreground transition-colors hover:text-foreground"
                    >
                      Skip this step
                    </button>
                  </div>
                </div>
              )}

              {/* STEP: Website URL */}
              {currentStep === 'website' && (
                <div className="animate-in fade-in slide-in-from-bottom-3 duration-400 ease-out">
                  <div className="space-y-2">
                    <span className="text-sm font-medium text-foreground">
                      {role === 'investor' ? "Your fund's website" : "Your startup's website"}
                    </span>
                    <div className="relative">
                      <input
                        type="url"
                        inputMode="url"
                        autoCapitalize="none"
                        autoCorrect="off"
                        autoComplete="url"
                        spellCheck={false}
                        value={website}
                        onChange={(e) => setWebsite(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && websiteValid && confirmWebsite()}
                        placeholder="yourcompany.com"
                        className={cn(
                          'flex h-10 w-full rounded-md border bg-card px-3 py-2 pr-10 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 transition-colors',
                          websiteValid
                            ? 'border-emerald-500/50 focus:border-emerald-500 focus:ring-emerald-500/30'
                            : 'border-border focus:border-[#f97316] focus:ring-[#f97316]'
                        )}
                      />
                      {websiteValid && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 animate-in fade-in zoom-in-50 duration-200">
                          <CheckCircle2 className="size-5 text-emerald-500" />
                        </div>
                      )}
                    </div>
                    <CtaButton
                      className="mt-1 w-full"
                      disabled={!websiteValid}
                      loading={loading}
                      arrow
                      onClick={confirmWebsite}
                    >
                      Continue
                    </CtaButton>
                    <button
                      type="button"
                      onClick={confirmWebsite}
                      className="w-full text-center text-xs text-muted-foreground transition-colors hover:text-foreground"
                    >
                      Skip this step
                    </button>
                  </div>
                </div>
              )}

              {/* STEP: Document upload */}
              {currentStep === 'document' && role && (
                <div className="space-y-3 animate-in fade-in slide-in-from-bottom-3 duration-400 ease-out">
                  <h2 className="text-center text-xl font-semibold text-foreground">
                    {role === 'investor'
                      ? 'Upload your investment thesis'
                      : 'Upload your pitch deck'}
                  </h2>
                  <p className="text-center text-sm text-muted-foreground">
                    {role === 'investor'
                      ? "We'll extract your investment focus and preferences"
                      : "We'll extract your startup details automatically"}
                  </p>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf"
                    className="hidden"
                    onChange={handleFileInputChange}
                  />

                  <div className="mt-4 space-y-3">
                    {!uploadedFile && !loading && (
                      <button
                        type="button"
                        className="w-full"
                        onClick={() => fileInputRef.current?.click()}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={handleDrop}
                      >
                        <div className="flex flex-col items-center gap-2 rounded-xl border-2 border-dashed border-border bg-card/30 px-6 py-6 transition-colors hover:border-[#f97316]/40 hover:bg-[#f97316]/[0.02]">
                          <Upload className="size-5 text-muted-foreground" />
                          <p className="text-sm font-medium text-foreground">
                            {role === 'investor'
                              ? 'Drop your investment thesis here'
                              : 'Drop your pitch deck here'}
                          </p>
                          <p className="text-xs text-muted-foreground">PDF, up to 50MB</p>
                        </div>
                      </button>
                    )}

                    {loading && (
                      <div className="flex flex-col items-center gap-2 py-4">
                        <Loader2 className="size-6 animate-spin text-[#f97316]" />
                        <p className="text-sm text-muted-foreground">Uploading...</p>
                      </div>
                    )}

                    {uploadedFile && !loading && (
                      <div className="animate-in fade-in slide-in-from-bottom-2 duration-300 space-y-3">
                        <div className="flex items-center gap-3 rounded-xl border border-emerald-500/30 bg-emerald-500/5 px-4 py-3">
                          <CheckCircle2 className="size-5 shrink-0 text-emerald-500" />
                          <span className="truncate text-sm font-medium text-foreground">
                            {uploadedFile.name}
                          </span>
                        </div>
                        <CtaButton
                          className="w-full"
                          arrow
                          onClick={() => {
                            onSubmit({
                              email,
                              linkedinUrl: resolvedLinkedinUrl,
                              websiteUrl: resolvedWebsiteUrl,
                              documentId: uploadedFile.documentId,
                              documentName: uploadedFile.name,
                              role,
                            })
                          }}
                        >
                          Continue
                        </CtaButton>
                      </div>
                    )}

                    <div className="flex items-center gap-3">
                      <div className="h-px flex-1 bg-border" />
                      <span className="text-xs text-muted-foreground/80">or</span>
                      <div className="h-px flex-1 bg-border" />
                    </div>

                    <SecondaryButton
                      className="w-full"
                      disabled={loading}
                      onClick={() => {
                        onSubmit({
                          email,
                          linkedinUrl: resolvedLinkedinUrl,
                          websiteUrl: resolvedWebsiteUrl,
                          documentId: null,
                          documentName: null,
                          role,
                        })
                      }}
                    >
                      Skip for now
                    </SecondaryButton>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </OnboardingLayout>
  )
}
